// @architect-approved: HARDCODED_COLORS - Dedicated dark "Executive Access" console theme (space/night gradient, cyan accent #4FC3F7) is intentionally distinct from the app's light white-label theme, shared with ExecutiveQuickMenu.tsx

// @architect-approved: INFRASTRUCTURE - ExecutiveQRLanding.tsx does not follow component identity pattern
/**
 * [CONTAINER-START]
 * CONTAINER-TYPE: form
 * CONTAINER-CLASS: public-landing
 * ROUTE: /qr/:qrCode
 * NAV-LABEL: QR Lead Capture
 * TG-SOLUTION-ID: 106
 * ASSOCIATION-TYPE: public-no-auth
 * DESCRIPTION: Public QR code landing page - Capture lead info from event scans
 * DEPENDENCIES: Executive Access API
 * CONTAINER-STATUS: mvp-production
 * CONTAINER-VERSION: 1.0.0
 * [CONTAINER-END]
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Box,
    Container,
    Typography,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Checkbox,
    FormControlLabel,
    Divider,
} from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';

interface QRCodeDetails {
    qr_code: string;
    campaign_name: string;
    lead_source: string;
    event_name?: string;
    event_location?: string;
    redirect_url?: string;
    is_active: boolean;
}

interface LeadForm {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company: string;
    jobTitle: string;
    interests: string;
    subscribeNewsletter: boolean;
    agreeToContact: boolean;
}

const ExecutiveQRLanding = () => {
    const { qrCode } = useParams<{ qrCode: string }>();
    const [qrDetails, setQrDetails] = useState<QRCodeDetails | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [formData, setFormData] = useState<LeadForm>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        jobTitle: '',
        interests: '',
        subscribeNewsletter: true,
        agreeToContact: false,
    });

    useEffect(() => {
        loadQRCodeDetails();
    }, [qrCode]);

    const loadQRCodeDetails = async () => {
        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || ''}/api/executive/qr/${qrCode}`,
            );

            if (!response.ok) {
                if (response.status === 404) {
                    toast.error('QR code not found or inactive');
                } else {
                    throw new Error('Failed to load QR code details');
                }
                return;
            }

            const data = await response.json();
            setQrDetails(data);
        } catch (error) {
            toast.error('Failed to load landing page');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.agreeToContact) {
            toast.error('Please agree to be contacted');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || ''}/api/executive/capture-lead`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        qr_code: qrCode,
                        first_name: formData.firstName,
                        last_name: formData.lastName,
                        email: formData.email,
                        phone: formData.phone || null,
                        company: formData.company || null,
                        job_title: formData.jobTitle || null,
                        interests: formData.interests || null,
                        subscribe_newsletter: formData.subscribeNewsletter,
                    }),
                },
            );

            if (!response.ok) throw new Error('Failed to submit lead');

            setIsSuccess(true);

            // Redirect after 3 seconds if redirect URL provided
            if (qrDetails?.redirect_url) {
                setTimeout(() => {
                    window.location.href = qrDetails.redirect_url!;
                }, 3000);
            }
        } catch (error) {
            toast.error('Failed to submit. Please try again.');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'radial-gradient(ellipse at center, #1a1a3e 0%, #0a0a1a 100%)',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!qrDetails || !qrDetails.is_active) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'radial-gradient(ellipse at center, #1a1a3e 0%, #0a0a1a 100%)',
                }}
            >
                <Container maxWidth="sm">
                    <Paper
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            background: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(15px)',
                            border: '2px solid rgba(244, 67, 54, 0.5)',
                        }}
                    >
                        <Typography variant="h5" sx={{ color: '#f44336', mb: 2 }}>
                            Invalid QR Code
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#B0BEC5' }}>
                            This QR code is either invalid or no longer active.
                        </Typography>
                    </Paper>
                </Container>
            </Box>
        );
    }

    if (isSuccess) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'radial-gradient(ellipse at center, #1a1a3e 0%, #0a0a1a 100%)',
                }}
            >
                <Container maxWidth="sm">
                    <Paper
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            background: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(15px)',
                            border: '2px solid rgba(76, 175, 80, 0.5)',
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 100, color: '#4CAF50', mb: 2 }} />
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
                            Thank You!
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#B0BEC5', mb: 2 }}>
                            Your information has been received. Our team will be in touch soon!
                        </Typography>
                        {qrDetails.redirect_url && (
                            <Typography variant="body2" sx={{ color: '#4FC3F7' }}>
                                Redirecting you shortly...
                            </Typography>
                        )}
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'radial-gradient(ellipse at center, #1a1a3e 0%, #0a0a1a 100%)',
                py: 4,
            }}
        >
            <Container maxWidth="md">
                <Paper
                    sx={{
                        background: 'rgba(0, 0, 0, 0.6)',
                        backdropFilter: 'blur(15px)',
                        border: '2px solid rgba(79, 195, 247, 0.3)',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #4FC3F7 0%, #2196F3 100%)',
                            p: 3,
                            textAlign: 'center',
                        }}
                    >
                        <QrCodeIcon sx={{ fontSize: 60, color: 'white', mb: 1 }} />
                        <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                            {qrDetails.campaign_name}
                        </Typography>
                        {qrDetails.event_name && (
                            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                {qrDetails.event_name}
                            </Typography>
                        )}
                        {qrDetails.event_location && (
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                {qrDetails.event_location}
                            </Typography>
                        )}
                    </Box>

                    {/* Form */}
                    <Box sx={{ p: 4 }}>
                        <Alert
                            severity="info"
                            sx={{ mb: 3, background: 'rgba(33, 150, 243, 0.1)' }}
                        >
                            <Typography variant="body2" sx={{ color: 'white' }}>
                                Thanks for your interest! Please share your information and we'll
                                get in touch with you soon.
                            </Typography>
                        </Alert>

                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                {/* Name Fields */}
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        label="First Name"
                                        value={formData.firstName}
                                        onChange={(e) =>
                                            setFormData({ ...formData, firstName: e.target.value })
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(79, 195, 247, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#4FC3F7',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        label="Last Name"
                                        value={formData.lastName}
                                        onChange={(e) =>
                                            setFormData({ ...formData, lastName: e.target.value })
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(79, 195, 247, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#4FC3F7',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Email */}
                                <Grid size={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="email"
                                        label="Email Address"
                                        value={formData.email}
                                        onChange={(e) =>
                                            setFormData({ ...formData, email: e.target.value })
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(79, 195, 247, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#4FC3F7',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Phone */}
                                <Grid size={12}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        value={formData.phone}
                                        onChange={(e) =>
                                            setFormData({ ...formData, phone: e.target.value })
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(79, 195, 247, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#4FC3F7',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Company Info */}
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        label="Company"
                                        value={formData.company}
                                        onChange={(e) =>
                                            setFormData({ ...formData, company: e.target.value })
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(79, 195, 247, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#4FC3F7',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                        }}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <BusinessIcon
                                                        sx={{
                                                            mr: 1,
                                                            color: 'rgba(255, 255, 255, 0.5)',
                                                        }}
                                                    />
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        label="Job Title"
                                        value={formData.jobTitle}
                                        onChange={(e) =>
                                            setFormData({ ...formData, jobTitle: e.target.value })
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(79, 195, 247, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#4FC3F7',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Interests */}
                                <Grid size={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="What are you interested in learning more about?"
                                        value={formData.interests}
                                        onChange={(e) =>
                                            setFormData({ ...formData, interests: e.target.value })
                                        }
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                '& fieldset': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'rgba(79, 195, 247, 0.5)',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#4FC3F7',
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                            {/* Consent Checkboxes */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.subscribeNewsletter}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                subscribeNewsletter: e.target.checked,
                                            })
                                        }
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            '&.Mui-checked': { color: '#4FC3F7' },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ color: '#B0BEC5' }}>
                                        Subscribe to our newsletter for updates and insights
                                    </Typography>
                                }
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        required
                                        checked={formData.agreeToContact}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                agreeToContact: e.target.checked,
                                            })
                                        }
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            '&.Mui-checked': { color: '#4FC3F7' },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ color: '#B0BEC5' }}>
                                        I agree to be contacted by Techgacity regarding this inquiry
                                        *
                                    </Typography>
                                }
                            />

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isSubmitting}
                                sx={{
                                    mt: 3,
                                    py: 1.5,
                                    background: 'linear-gradient(135deg, #4FC3F7 0%, #2196F3 100%)',
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    '&:hover': {
                                        background:
                                            'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                                    },
                                    '&:disabled': {
                                        background: 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
                            </Button>
                        </form>
                    </Box>
                </Paper>

                {/* Footer */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="body2" sx={{ color: '#B0BEC5' }}>
                        Lead Source: {qrDetails.lead_source}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', mt: 1 }}>
                        © 2026 Techgacity. All rights reserved.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default ExecutiveQRLanding;
